<script setup lang="ts">
import { useOrderStatus } from '~/composables/useOrderStatus'
import { RestaurantAreas } from '~/lib/interfaces/restaurant'
import { StoreOrder } from '~/lib/services/store/order/order.dto'
import { canPickupSpecialOrder } from '~/utils/orderUtils'
import { transformOrderDate } from '~/utils/datesUtils'

const props = withDefaults(
  defineProps<{
    order: StoreOrder
    fetching: boolean
    isLast?: boolean
  }>(),
  {
    isLast: false,
  }
)

const isMobile = useIsMobile()
const { getStatus, selectIcon } = useOrderStatus()
const { t } = useLocale()
const { orderDetailRoute } = useMcdRoutes()
const { trackTrackMyOrder, trackClickOrderDetail } = useGTM()

const item = ref<HTMLElement>()
const observer = ref<IntersectionObserver>()
const orderState = ref(getOrderState(props.order))

const navigateToOrderDetails = (orderId: string) => {
  trackClickOrderDetail({ order: props.order })
  return navigateTo(orderDetailRoute(orderId))
}
const emit = defineEmits([
  'payOrder',
  'cancelOrder',
  'repeatOrder',
  'arrivalRestaurant',
  'rateOrder',
  'receiveOrder',
  'load-more',
])

const orderStatus = computed(() => getStatus(props.order.subStatus || props.order.status))

const orderAddress = computed(() =>
  buildFormattedAddress(props.order.deliveryPlace, props.order.area)
)

const showPickupButton = computed(() => {
  return (
    props.order.type === RestaurantAreas.MOP &&
    (orderState.value.isPaymentDone || canPickupSpecialOrder(props.order))
  )
})

const hasAnyAction = computed(() => {
  return (
    orderState.value.canCancel ||
    orderState.value.isPending ||
    orderState.value.canRepeat ||
    orderState.value.isRateable ||
    showPickupButton.value ||
    orderState.value.isTrackable ||
    orderState.value.isReceiveInProgress
  )
})

const trackOrder = () => {
  const trackingUrl = props.order ? getTrackingUrl(props.order) : undefined
  if (trackingUrl) {
    trackTrackMyOrder()
    window.open(trackingUrl, '_blank')
  }
}

watch(
  () => props.order.status,
  () => {
    orderState.value = getOrderState(props.order)
  }
)

onMounted(() => {
  if (item.value && props.isLast) {
    // Add observer for lazy load
    observer.value = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          emit('load-more')
          observer.value?.disconnect()
        }
      },
      {
        root: null,
        threshold: 0.5,
      }
    )
    observer.value.observe(item.value)
  }

  onUnmounted(() => {
    if (observer.value) observer?.value?.disconnect()
  })
})
</script>

<template>
  <div ref="item" data-test="order-card">
    <div v-if="isMobile" class="mb-4 rounded-md border shadow-sm">
      <section class="flex flex-col p-4" @click="navigateToOrderDetails(order.id)">
        <header class="flex w-full justify-between">
          <h3 class="text-xs" :class="orderStatus.style">
            {{ orderStatus.text }}
          </h3>
          <h3 class="text-xs">{{ transformOrderDate(order.createdAt) }}</h3>
        </header>

        <section>
          <figure class="flex gap-2 py-3">
            <img class="h-9 w-9" :src="selectIcon(order.type).icon" alt="food" />
            <div>
              <h2 class="text-sm font-bold">{{ selectIcon(order.type).text }}</h2>
              <h3 class="line-clamp-1 text-xs text-mcd-secondaryDarkGrey">
                {{ orderAddress }}
              </h3>
            </div>
          </figure>

          <h3 class="line-clamp-1 text-xs text-mcd-secondaryDarkGrey">
            {{ order.products.map(product => product.name).join(', ') }}
          </h3>
        </section>
      </section>

      <footer v-if="hasAnyAction" class="flex gap-4 border-t-2 p-4">
        <McdButton
          v-if="orderState.canCancel"
          :disabled="fetching"
          class="w-full"
          @on-click="emit('cancelOrder')"
        >
          {{ t('orders.buttons.cancelOrder') }}
        </McdButton>

        <McdButton
          v-if="orderState.isPending"
          is-primary
          :disabled="fetching"
          class="w-full"
          @on-click="emit('payOrder')"
        >
          {{ t('orders.buttons.pay') }}
        </McdButton>

        <McdButton
          v-if="orderState.canRepeat"
          is-primary
          :disabled="fetching"
          class="w-full"
          @on-click="emit('repeatOrder')"
        >
          {{ t('orders.buttons.reorder') }}
        </McdButton>

        <McdButton
          v-if="orderState.isRateable"
          :disabled="fetching"
          class="w-full"
          @on-click="emit('rateOrder')"
        >
          {{ t('orders.buttons.rate') }}
        </McdButton>

        <McdButton
          v-if="showPickupButton"
          is-primary
          :disabled="fetching || !orderState.canPickup"
          class="w-full"
          @on-click="emit('arrivalRestaurant')"
        >
          {{ t('orders.buttons.arrival') }}
        </McdButton>

        <McdButton
          v-if="orderState.isTrackable"
          is-primary
          :disabled="fetching"
          class="w-full"
          @on-click="trackOrder"
        >
          {{ t('orders.buttons.track') }}
        </McdButton>

        <McdButton
          v-if="orderState.isReceiveInProgress"
          is-primary
          :disabled="!orderState.canReceive || fetching"
          class="w-full"
          @on-click="emit('receiveOrder')"
        >
          {{ t('orders.buttons.receive') }}
        </McdButton>
      </footer>
    </div>

    <div v-else class="flex w-full flex-wrap gap-6 pb-8 pt-6 lg:flex-nowrap">
      <!-- GRID -->
      <section class="custom-grid-template">
        <header class="custom-grid-template-inside col-span-3 row-start-1">
          <h4 class="col-span-1 text-xs">{{ transformOrderDate(order.createdAt) }}</h4>
          <h4 class="col-span-2 col-start-2 text-xs" :class="orderStatus.style">
            {{ orderStatus.text }}
          </h4>
        </header>

        <section class="custom-grid-template-inside col-span-3 row-start-2 grid-rows-1">
          <figure class="col-span-1 flex justify-center">
            <img class="h-9 w-9" :src="selectIcon(order.type).icon" alt="food" />
          </figure>
          <div class="col-span-2">
            <h3 class="text-sm font-bold">{{ selectIcon(order.type).text }}</h3>
            <h3 class="line-clamp-1 text-xs text-mcd-secondaryDarkGrey">
              {{ orderAddress }}
            </h3>
          </div>
        </section>

        <div class="col-span-2 col-start-2 row-start-3">
          <h4 class="line-clamp-1 text-[10px] text-mcd-secondaryDarkGrey">
            {{ order.products.map(product => product.name).join(', ') }}
          </h4>
        </div>

        <footer class="col-span-2 col-start-2 row-start-4">
          <button
            class="text-xs text-mcd-secondaryDarkGrey underline"
            @click="navigateToOrderDetails(order.id)"
          >
            {{ t('common.seeDetail') }}
          </button>
        </footer>
      </section>

      <!-- BUTTONS -->
      <section v-if="hasAnyAction" class="buttons-flex">
        <McdButton
          v-if="orderState.canCancel"
          class="w-fit min-w-[6rem] whitespace-nowrap px-2 py-1 text-2xs"
          :disabled="fetching"
          @on-click="emit('cancelOrder')"
        >
          {{ t('orders.buttons.cancelOrder') }}
        </McdButton>
        <McdButton
          v-if="orderState.isPending"
          is-primary
          :disabled="fetching"
          class="w-fit min-w-[6rem] whitespace-nowrap px-2 py-1 text-2xs"
          @on-click="emit('payOrder')"
        >
          {{ t('orders.buttons.pay') }}
        </McdButton>
        <McdButton
          v-if="orderState.canRepeat"
          is-primary
          :disabled="fetching"
          class="w-fit min-w-[6rem] whitespace-nowrap px-2 py-1 text-2xs"
          @on-click="emit('repeatOrder')"
        >
          {{ t('orders.buttons.reorder') }}
        </McdButton>
        <McdButton
          v-if="orderState.isRateable"
          :disabled="fetching"
          class="w-fit min-w-[6rem] whitespace-nowrap px-2 py-1 text-2xs"
          @on-click="emit('rateOrder')"
        >
          {{ t('orders.buttons.rate') }}
        </McdButton>
        <McdButton
          v-if="showPickupButton"
          is-primary
          :disabled="fetching || !orderState.canPickup"
          class="w-fit min-w-[6rem] whitespace-nowrap px-2 py-1 text-2xs"
          @on-click="emit('arrivalRestaurant')"
        >
          {{ t('orders.buttons.arrival') }}
        </McdButton>
        <McdButton
          v-if="orderState.isTrackable"
          is-primary
          :disabled="fetching"
          class="w-fit min-w-[6rem] whitespace-nowrap px-2 py-1 text-2xs"
          @on-click="trackOrder"
        >
          {{ t('orders.buttons.track') }}
        </McdButton>

        <McdButton
          v-if="orderState.isReceiveInProgress"
          is-primary
          :disabled="!orderState.canReceive || fetching"
          class="w-fit min-w-[6rem] whitespace-nowrap px-2 py-1 text-2xs"
          @on-click="emit('receiveOrder')"
        >
          {{ t('orders.buttons.receive') }}
        </McdButton>
      </section>
    </div>
  </div>
</template>

<style>
.custom-grid-columns {
  @apply grid-cols-[70px_auto_auto] gap-4;
}

.custom-grid-rows {
  @apply grid-rows-[0.5fr_auto_0.5fr_0.5fr];
}

.buttons-flex {
  @apply flex w-4/12 items-center justify-end gap-5 md:w-full;
}

.custom-grid-template {
  @apply custom-grid-columns custom-grid-rows grid w-8/12 md:w-full;
}

.custom-grid-template-inside {
  @apply custom-grid-columns grid;
}
</style>
